import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconButton, Typography, Box, Grid, Skeleton, CircularProgress } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import defaultBook from "../../Images/defaultBook.png";
import KalyansService from '../../Services/Kalyan';
import KalpataruServices from '../../Services/Kalpataru';
import {
    HeaderBox,
    MainContentBox,
    BookGridContainer,
    BookItemBox,
    BookImage,
    BookTitleTypography,
    SkeletonBookGridContainer,
    SkeletonBookItemBox,
} from './mBookListPage';

const PAGE_SIZE = 20;

/**
 * Mobile Kalyan / Kalpataru issue list for a selected category.
 * Step 3 in Magazines → Kalyan/Kalpataru → category → home flow.
 */
const MMagazineListPage = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const location: any = useLocation();
    const { catid } = useParams();

    const [items, setItems] = useState<any[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [activeSortOption] = useState(location?.state?.sortOption || '3');
    const [isGridView, setIsGridView] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const observerRef = useRef<HTMLDivElement>(null);

    const isKalpataruPath = location.pathname.startsWith('/magazines/kalyanakalpataru');
    const isKalpataruBook = !!location?.state?.isKalpataruBook || isKalpataruPath;
    const categoryId = catid || location?.state?.kalyansId || '';

    const fetchItems = useCallback(async (pageNum: number, append: boolean) => {
        const logdetails: string[] = [
            `fetch_magazine_list page=${pageNum}`,
            `append=${append}`,
            `categoryId=${categoryId}`,
            `isKalpataru=${isKalpataruBook}`,
        ];

        if (append) setIsLoadingMore(true);
        else setIsLoading(true);

        try {
            const offset = pageNum * PAGE_SIZE;
            const rawLanguageId = location?.state?.kalyansLanguage || location?.state?.magazineLanguage || '';
            const resolvedLanguageId = (rawLanguageId === 'hi' || rawLanguageId === 'en') ? '' : rawLanguageId;

            let res: any;
            if (isKalpataruBook) {
                res = await KalpataruServices.getKalyansKalpataru(
                    offset, PAGE_SIZE, false, categoryId, '', '', activeSortOption, resolvedLanguageId, false
                );
                logdetails.push('api=getKalyansKalpataru');
            } else {
                res = await KalyansService.getKalyans(
                    offset, PAGE_SIZE, false, categoryId, '', '', activeSortOption, resolvedLanguageId, false
                );
                logdetails.push('api=getKalyans');
            }

            if (res?.status && res.result) {
                const newItems = (res.result.items || []).map((item: any) => ({
                    title: item.name,
                    img: item.kalyanThumbPath || item.kalyanKalpataruThumbPath || defaultBook,
                    author: item.author,
                    id: item.id,
                    slug: item.slug,
                }));
                const total = res.result.totalRecords || 0;

                if (append) {
                    setItems(prev => [...prev, ...newItems]);
                } else {
                    setItems(newItems);
                }
                setTotalItems(total);
                setHasMore((pageNum * PAGE_SIZE + newItems.length) < total);
                logdetails.push(`items=${newItems.length} total=${total}`);
            } else if (!append) {
                setItems([]);
                setTotalItems(0);
                setHasMore(false);
                logdetails.push(`list_failed message=${res?.message || 'unknown'}`);
            }
        } catch (error) {
            logdetails.push(`list_error=${error}`);
            console.error('Error fetching magazine list items:', error);
            if (!append) {
                setItems([]);
                setTotalItems(0);
                setHasMore(false);
            }
        } finally {
            setIsLoading(false);
            setIsLoadingMore(false);
            console.log('[MMagazineListPage]', logdetails.join(' | '));
        }
    }, [
        categoryId,
        isKalpataruBook,
        activeSortOption,
        location?.state?.kalyansLanguage,
        location?.state?.magazineLanguage,
        i18n.language,
    ]);

    useEffect(() => {
        setPage(0);
        setHasMore(true);
        fetchItems(0, false);
    }, [fetchItems]);

    const loadMore = useCallback(async () => {
        if (!hasMore || isLoadingMore || isLoading) return;
        const nextPage = page + 1;
        setPage(nextPage);
        await fetchItems(nextPage, true);
    }, [page, hasMore, isLoadingMore, isLoading, fetchItems]);

    useEffect(() => {
        const node = observerRef.current;
        if (!node) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !isLoadingMore && !isLoading) {
                    loadMore();
                }
            },
            { threshold: 0.1 }
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, [loadMore, hasMore, isLoadingMore, isLoading]);

    // Navigate to magazine home (detail) for selected Kalyan / Kalpataru issue
    const handleItemClick = (item: { id: string; slug?: string; title?: string }) => {
        const logdetails: string[] = [`item_click id=${item.id}`];
        const slugOrId = item.slug || item.id;

        if (isKalpataruBook) {
            logdetails.push('type=kalyan_kalpataru');
            logdetails.push(`navigate=/magazines/kalyanakalpataru/${slugOrId}`);
            navigate(`/magazines/kalyanakalpataru/${slugOrId}`, {
                state: {
                    kalpataruId: item.id,
                    kalyansId: item.id,
                    magzineId: item.id,
                    isKalpataruBook: true,
                    bookName: item.title,
                    slug: item.slug,
                    type: 'kalyanakalpataru',
                    // Preserve category so View All returns to mMagazineListPage
                    categoryId,
                    kalyanName: location?.state?.kalyanName || location?.state?.kalpataruName,
                    kalpataruName: location?.state?.kalpataruName || location?.state?.kalyanName,
                    kalyansLanguage: location?.state?.kalyansLanguage,
                },
            });
        } else {
            logdetails.push('type=kalyan');
            logdetails.push(`navigate=/magazines/kalyan/${slugOrId}`);
            navigate(`/magazines/kalyan/${slugOrId}`, {
                state: {
                    kalyansId: item.id,
                    magzineId: item.id,
                    bookName: item.title,
                    slug: item.slug,
                    isKalpataruBook: false,
                    kalyansLanguage: location?.state?.kalyansLanguage,
                    type: 'kalyan',
                    // Preserve category so View All returns to mMagazineListPage
                    categoryId,
                    kalyanName: location?.state?.kalyanName,
                },
            });
        }

        console.log('[MMagazineListPage]', logdetails.join(' | '));
    };

    const numberOfSkeletons = isGridView ? 9 : 5;
    const selectedName = location?.state?.kalyanName || location?.state?.kalpataruName || null;
    const baseTitleKey = isKalpataruBook ? 'Kalpataru_lang_tr' : 'All_Kalyans';
    const baseTitle = t(baseTitleKey);

    return (
        <Box sx={{
            backgroundColor: '#F2F0EF',
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
        }}>
            <HeaderBox isSearchActive={false}>
                <>
                    <IconButton onClick={() => navigate(-1)} sx={{ position: 'absolute', left: 16, top: 35, p: 0 }}>
                        <ArrowBackIosIcon sx={{ color: 'white', fontSize: '18px' }} />
                    </IconButton>
                    <IconButton onClick={() => navigate('/')} sx={{ position: 'absolute', left: 48, top: 35, p: 0 }}>
                        <HomeOutlinedIcon sx={{ color: 'white', fontSize: '24px' }} />
                    </IconButton>

                    <IconButton
                        onClick={() => setIsGridView(prev => !prev)}
                        sx={{ position: 'absolute', right: 16, top: 35, p: 0 }}
                    >
                        {isGridView ? (
                            <ViewListIcon sx={{ color: 'white', fontSize: '20px' }} />
                        ) : (
                            <ViewModuleIcon sx={{ color: 'white', fontSize: '20px' }} />
                        )}
                    </IconButton>
                    <Typography variant="caption" sx={{ color: '#ffffffdd', opacity: 0.9, mb: 0.5 }}>
                        {t('shrihari_tr')}
                    </Typography>
                    {/* Kalyan / Kalpataru: base title + category name on two lines */}
                    <Typography
                        variant="h6"
                        sx={{
                            color: '#ffffffdd',
                            opacity: 0.9,
                            fontSize: 15,
                            lineHeight: 1.25,
                            textAlign: 'center',
                            px: 6,
                        }}
                    >
                        {selectedName ? (
                            <>
                                {baseTitle}
                                <br />
                                ({selectedName})
                            </>
                        ) : baseTitle}
                    </Typography>
                </>
            </HeaderBox>

            <Typography variant="body2" sx={{ textAlign: 'center', mt: 0.5, fontWeight: 500, fontFamily: 'chanakyaUni', fontSize: 16, height: 15, padding: -3 }}>
                Total Records: {totalItems}
            </Typography>

            <MainContentBox>
                {isLoading ? (
                    <SkeletonBookGridContainer container spacing={1} isGridView={isGridView}>
                        {Array.from(new Array(numberOfSkeletons)).map((_, index) => (
                            <Grid
                                item
                                xs={isGridView ? 4 : 12}
                                sm={isGridView ? 3 : 12}
                                md={isGridView ? 2 : 12}
                                key={index}
                            >
                                <SkeletonBookItemBox isGridView={isGridView}>
                                    <Skeleton
                                        variant="rectangular"
                                        width={isGridView ? '100%' : '60px'}
                                        height={isGridView ? '120px' : '80px'}
                                        sx={{
                                            marginBottom: isGridView ? '8px' : '0',
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                        <Skeleton variant="text" sx={{ fontSize: isGridView ? '12px' : '14px', width: '80%', mb: 0.5 }} />
                                    </Box>
                                </SkeletonBookItemBox>
                            </Grid>
                        ))}
                    </SkeletonBookGridContainer>
                ) : items.length > 0 ? (
                    <BookGridContainer container spacing={isGridView ? 1 : 0} isGridView={isGridView}>
                        {items.map((item: any) => (
                            <Grid
                                item
                                xs={isGridView ? 4 : 12}
                                sm={isGridView ? 3 : 12}
                                md={isGridView ? 2 : 12}
                                key={item.id}
                            >
                                <BookItemBox isGridView={isGridView} onClick={() => handleItemClick(item)}>
                                    <BookImage
                                        src={item.img || defaultBook}
                                        alt={item.title}
                                        onError={(e: any) => { e.currentTarget.src = defaultBook; }}
                                        isGridView={isGridView}
                                    />
                                    <BookTitleTypography variant="body2" isGridView={isGridView} sx={{ whiteSpace: 'normal', display: '-webkit-box', WebkitLineClamp: isGridView ? 3 : 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                        {item.title && item.title.includes('(') ? (
                                            <>
                                                {item.title.split('(')[0].trim()}
                                                <br />
                                                ({item.title.split('(')[1]}
                                            </>
                                        ) : item.title}
                                    </BookTitleTypography>
                                </BookItemBox>
                            </Grid>
                        ))}
                    </BookGridContainer>
                ) : (
                    <Typography variant="body1" sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
                        {t('result_not_found_tr')}
                    </Typography>
                )}

                {isLoadingMore && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                        <CircularProgress size={24} />
                    </Box>
                )}
                {hasMore && <Box ref={observerRef} sx={{ height: 1 }} />}
            </MainContentBox>
        </Box>
    );
};

export default MMagazineListPage;
